import { memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { A11y, EffectFlip } from 'swiper';

import { ImageEmbedded } from '../image/image-embedded';
import { useIsInEditorMode } from '../../utils/use-is-in-editor-mode';
import { useIsHydrated } from '../../utils/context/hydration.context';

import { LogoProps, TIME_RANGE_MAX, TIME_RANGE_MIN, TIME_UNIT } from './animated-logo.config';

SwiperCore.use([A11y, EffectFlip]);

const AnimatedLogo = memo(({ additionalClasses, isInEditor, logoItems, showAnimation }: LogoProps) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [timeRangeRandom, setTimeRangeRandom] = useState(TIME_RANGE_MIN * TIME_UNIT);
  const swiperRef = useRef<SwiperCore | null>(null);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const isHydrated = useIsHydrated();

  const { isInEditorMode } = useIsInEditorMode(isInEditor);

  useEffect(() => {
    setTimeRangeRandom((Math.random() * (TIME_RANGE_MAX - TIME_RANGE_MIN) + TIME_RANGE_MIN) * TIME_UNIT);
  }, []);

  useEffect(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    if (showAnimation && !isInEditorMode && logoItems.length > 1 && swiperRef.current) {
      timeoutIdRef.current = setTimeout(() => {
        setIsFlipped((prevFlipped) => !prevFlipped);
        swiperRef.current?.slideNext();
      }, timeRangeRandom);
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [isFlipped, timeRangeRandom, showAnimation, isInEditorMode, logoItems.length]);

  const fallbackLogoItem = (
    <div data-testid="snowflake-animated-logo-inner" className="snowflake-animated-logo-inner">
      {logoItems.length > 0 && <ImageEmbedded {...logoItems[0]} baseCssClass="cmp-image" />}
    </div>
  );

  const handleSwiperInit = useCallback((swiperCore: SwiperCore) => {
    swiperRef.current = swiperCore;
  }, []);

  const logoFlipCarousel = (
    <Swiper
      effect="flip"
      speed={1500}
      loop
      autoplay={false}
      grabCursor
      onSwiper={handleSwiperInit}
      flipEffect={{
        slideShadows: false,
        limitRotation: true,
      }}
    >
      {logoItems.map((logoItem) => (
        <SwiperSlide data-testing="snowflake-animated-logo-slide" tag="span" key={logoItem.id}>
          <div className="snowflake-animated-logo-inner">
            <ImageEmbedded {...logoItem} baseCssClass="cmp-image" />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );

  return (
    <div
      data-testid="snowflake-animated-logo"
      className={classNames('snowflake-animated-logo snowflake-logo', additionalClasses)}
    >
      <div data-testid="snowflake-animated-logo-flipper" className="snowflake-animated-logo-flipper">
        {isInEditorMode || !showAnimation || logoItems.length <= 1 || !isHydrated ? fallbackLogoItem : logoFlipCarousel}
      </div>
    </div>
  );
});

AnimatedLogo.displayName = 'AnimatedLogo';

export default AnimatedLogo;
